/**
 * Pedidos de clientes. MODULO ADITIVO E INDEPENDIENTE.
 *
 * Responde la pregunta de la entrega: a quien le toca cada plato, cuanto debe,
 * si ya pago y si ya se lo llevo.
 *
 * LO QUE ESTE MODULO NO HACE, a proposito:
 *
 *   - No escribe en `sales`, `productions`, `productionItems` ni
 *     `inventoryMovements`. Crear, editar, cobrar o entregar un pedido no mueve
 *     inventario, no genera producciones y no registra ventas.
 *   - No modifica `plannedItems`. Las porciones que se cocinan siguen saliendo
 *     del menu; las del pedido son lo que una persona aparto. Son datos
 *     distintos y deliberadamente independientes.
 *   - No toca `recipes`. La receta se lee para saber el nombre y sugerir el
 *     precio, nada mas.
 *
 * Motivo: el ingreso del negocio nace unicamente al registrar una venta y el
 * costo se congela al completar una produccion, con un prorrateo acumulado que
 * evita contar el mismo costo dos veces. Si un pedido escribiera en esas tablas
 * por su cuenta, habria ingreso duplicado, costo descuadrado o inventario
 * descontado dos veces.
 *
 * El campo `saleId` queda reservado para cuando se decida conectar la entrega
 * con `registrarVenta()`. Hoy siempre es null.
 */

import { db } from '../schema';
import { nuevoId } from '../ids';
import {
  exigirTexto,
  exigirFecha,
  exigirCantidadPositiva,
  exigirImporteNoNegativo,
  exigirUnoDe,
  exigirExiste,
  textoOpcional,
} from '../validation';
import { ErrorValidacion } from '@/domain/errors';
import { ahoraIso, hoy, exigirFechaLocal, type FechaLocal } from '@/domain/dates';
import { type Centavos } from '@/domain/money';
import {
  totalDelPedido,
  resumenDePedidos,
  agruparPorFechaEntrega,
  agruparPorPlatillo,
  resumenDeEntregas,
  cuentasPorCobrar,
  claveDeCliente,
  nombreParaMostrar,
  type EstadoPago,
  type EstadoEntrega,
  type ResumenPedidos,
  type DiaDePedidos,
  type PlatilloDeEntrega,
  type ResumenEntregas,
  type CuentasPorCobrar,
} from '@/domain/orders';
import type { Order } from '../types';

const ESTADOS_PAGO_VALIDOS = ['pendiente', 'pagado'] as const;
const ESTADOS_ENTREGA_VALIDOS = ['pendiente', 'entregado'] as const;

const MAX_NOMBRE = 80;
const MAX_NOTAS = 200;

// ---------------------------------------------------------------------------
// Alta
// ---------------------------------------------------------------------------

export interface DatosNuevoPedido {
  clienteNombre: string;
  fechaEntrega: FechaLocal;
  recipeId: string;
  porciones: number;
  /** Si no se indica, se toma el precio de venta de la receta. */
  precioPorPorcionCentavos?: Centavos;
  /** Enlace opcional con el platillo del menu de ese dia. */
  plannedItemId?: string | null;
  estadoPago?: EstadoPago;
  estadoEntrega?: EstadoEntrega;
  notas?: string | null;
}

export async function crearPedido(datos: DatosNuevoPedido): Promise<Order> {
  const clienteNombre = exigirTexto(datos.clienteNombre, 'nombre del cliente', {
    maximo: MAX_NOMBRE,
  });
  const fechaEntrega = exigirFecha(datos.fechaEntrega, 'fecha de entrega');
  const porciones = exigirCantidadPositiva(datos.porciones, 'porciones');

  // La receta debe existir: sin ella el pedido no sabria de que platillo es.
  const receta = await exigirExiste(db.recipes, datos.recipeId, 'la receta');

  const precio = exigirImporteNoNegativo(
    datos.precioPorPorcionCentavos ?? receta.precioVentaCentavos,
    'precio por porcion',
  );

  const plannedItemId = await validarPlatillo(datos.plannedItemId ?? null, fechaEntrega);

  const ahora = ahoraIso();
  const pedido: Order = {
    id: nuevoId('ped'),
    clienteNombre,
    fechaEntrega,
    recipeId: receta.id,
    plannedItemId,
    porciones,
    precioPorPorcionCentavos: precio,
    totalCentavos: totalDelPedido(porciones, precio),
    estado: 'activo',
    estadoPago: exigirUnoDe(datos.estadoPago ?? 'pendiente', ESTADOS_PAGO_VALIDOS, 'estado de pago'),
    estadoEntrega: exigirUnoDe(
      datos.estadoEntrega ?? 'pendiente',
      ESTADOS_ENTREGA_VALIDOS,
      'estado de entrega',
    ),
    saleId: null,
    notas: textoOpcional(datos.notas, 'notas', MAX_NOTAS),
    creadoEn: ahora,
    actualizadoEn: ahora,
  };

  await db.orders.add(pedido);
  return pedido;
}

/**
 * El platillo del menu es opcional, pero si se indica tiene que existir y ser
 * del mismo dia de entrega. Ligarlo a otro dia solo confundiria al leerlo.
 */
async function validarPlatillo(
  plannedItemId: string | null,
  fechaEntrega: FechaLocal,
): Promise<string | null> {
  if (plannedItemId === null) return null;

  const platillo = await exigirExiste(db.plannedItems, plannedItemId, 'el platillo del menu');
  if (platillo.fechaVenta !== fechaEntrega) {
    throw new ErrorValidacion(
      'El platillo del menu que elegiste se vende el ' +
        `${platillo.fechaVenta} y el pedido es para el ${fechaEntrega}.`,
      'plannedItemId',
    );
  }
  return platillo.id;
}

// ---------------------------------------------------------------------------
// Edicion
// ---------------------------------------------------------------------------

export interface CambiosPedido {
  clienteNombre?: string;
  fechaEntrega?: FechaLocal;
  recipeId?: string;
  porciones?: number;
  precioPorPorcionCentavos?: Centavos;
  plannedItemId?: string | null;
  estadoPago?: EstadoPago;
  estadoEntrega?: EstadoEntrega;
  notas?: string | null;
}

/**
 * Edita un pedido. Se puede corregir todo, incluida la fecha.
 *
 * El total se recalcula solo si cambian las porciones o el precio. Editar el
 * nombre no vuelve a costear nada.
 */
export async function editarPedido(id: string, cambios: CambiosPedido): Promise<Order> {
  const pedido = await exigirExiste(db.orders, id, 'el pedido');
  const parche: Partial<Order> = {};

  if (cambios.clienteNombre !== undefined) {
    parche.clienteNombre = exigirTexto(cambios.clienteNombre, 'nombre del cliente', {
      maximo: MAX_NOMBRE,
    });
  }

  if (cambios.recipeId !== undefined && cambios.recipeId !== pedido.recipeId) {
    const receta = await exigirExiste(db.recipes, cambios.recipeId, 'la receta');
    parche.recipeId = receta.id;
  }

  const fechaEntrega =
    cambios.fechaEntrega === undefined
      ? pedido.fechaEntrega
      : exigirFecha(cambios.fechaEntrega, 'fecha de entrega');
  if (cambios.fechaEntrega !== undefined) parche.fechaEntrega = fechaEntrega;

  // Si se mueve la fecha y el pedido estaba ligado a un platillo de la fecha
  // anterior, el enlace deja de tener sentido y se suelta.
  if (cambios.plannedItemId !== undefined) {
    parche.plannedItemId = await validarPlatillo(cambios.plannedItemId, fechaEntrega);
  } else if (parche.fechaEntrega !== undefined && pedido.plannedItemId !== null) {
    const platillo = await db.plannedItems.get(pedido.plannedItemId);
    if (platillo === undefined || platillo.fechaVenta !== fechaEntrega) {
      parche.plannedItemId = null;
    }
  }

  const porciones =
    cambios.porciones === undefined
      ? pedido.porciones
      : exigirCantidadPositiva(cambios.porciones, 'porciones');
  if (cambios.porciones !== undefined) parche.porciones = porciones;

  const precio =
    cambios.precioPorPorcionCentavos === undefined
      ? pedido.precioPorPorcionCentavos
      : exigirImporteNoNegativo(cambios.precioPorPorcionCentavos, 'precio por porcion');
  if (cambios.precioPorPorcionCentavos !== undefined) parche.precioPorPorcionCentavos = precio;

  if (cambios.porciones !== undefined || cambios.precioPorPorcionCentavos !== undefined) {
    parche.totalCentavos = totalDelPedido(porciones, precio);
  }

  if (cambios.estadoPago !== undefined) {
    parche.estadoPago = exigirUnoDe(cambios.estadoPago, ESTADOS_PAGO_VALIDOS, 'estado de pago');
  }
  if (cambios.estadoEntrega !== undefined) {
    parche.estadoEntrega = exigirUnoDe(
      cambios.estadoEntrega,
      ESTADOS_ENTREGA_VALIDOS,
      'estado de entrega',
    );
  }
  if (cambios.notas !== undefined) {
    parche.notas = textoOpcional(cambios.notas, 'notas', MAX_NOTAS);
  }

  parche.actualizadoEn = ahoraIso();
  await db.orders.update(id, parche);

  return { ...pedido, ...parche };
}

// ---------------------------------------------------------------------------
// Estados rapidos
// ---------------------------------------------------------------------------

/**
 * Alterna pagado / pendiente sin abrir el pedido.
 *
 * Existe como operacion propia porque es el gesto mas frecuente del dia: se
 * cobra en la puerta y hay que marcarlo con una mano.
 */
export async function alternarPago(id: string, fechaCobro?: FechaLocal): Promise<EstadoPago> {
  const pedido = await exigirExiste(db.orders, id, 'el pedido');
  const nuevo: EstadoPago = pedido.estadoPago === 'pagado' ? 'pendiente' : 'pagado';

  /**
   * Al marcar pagado se guarda TAMBIEN el dia del cobro, y al desmarcar se limpia.
   *
   * `estadoPago` dice SI pago; la fecha dice CUANDO, y es lo que necesita la caja
   * para saber en que semana entro el dinero. Juan paga cada quince dias, asi que su
   * cobro cae en una semana distinta a la de la entrega.
   *
   * No sirve `actualizadoEn` para esto: es una bitacora y se sobreescribe con
   * cualquier otra edicion del pedido.
   *
   * La fecha se puede pasar por si se captura un cobro de ayer. Si no se pasa, es
   * hoy, que es el caso normal: se cobra en la puerta y se marca ahi mismo.
   */
  await db.orders.update(id, {
    estadoPago: nuevo,
    fechaPago: nuevo === 'pagado' ? exigirFechaLocal(fechaCobro ?? hoy(), 'fecha de cobro') : null,
    actualizadoEn: ahoraIso(),
  });
  return nuevo;
}

export async function alternarEntrega(id: string): Promise<EstadoEntrega> {
  const pedido = await exigirExiste(db.orders, id, 'el pedido');
  const nuevo: EstadoEntrega = pedido.estadoEntrega === 'entregado' ? 'pendiente' : 'entregado';
  await db.orders.update(id, { estadoEntrega: nuevo, actualizadoEn: ahoraIso() });
  return nuevo;
}

// ---------------------------------------------------------------------------
// Cancelar y borrar
// ---------------------------------------------------------------------------

/**
 * Cancela el pedido conservando el registro.
 *
 * Para cuando el cliente se arrepiente: el pedido existio y conviene poder
 * verlo. Deja de sumar en los totales del dia, pero sigue en la lista.
 */
export async function cancelarPedido(id: string, motivo?: string): Promise<Order> {
  const pedido = await exigirExiste(db.orders, id, 'el pedido');

  const nota = textoOpcional(motivo, 'motivo', MAX_NOTAS);
  const parche: Partial<Order> = { estado: 'cancelado', actualizadoEn: ahoraIso() };
  if (nota !== null) {
    parche.notas = pedido.notas === null ? nota : `${pedido.notas} · ${nota}`.slice(0, MAX_NOTAS);
  }

  await db.orders.update(id, parche);
  return { ...pedido, ...parche };
}

export async function reactivarPedido(id: string): Promise<Order> {
  const pedido = await exigirExiste(db.orders, id, 'el pedido');
  const parche: Partial<Order> = { estado: 'activo', actualizadoEn: ahoraIso() };
  await db.orders.update(id, parche);
  return { ...pedido, ...parche };
}

/**
 * Borra el pedido de verdad.
 *
 * Se reserva para el error de captura, que es distinto de una cancelacion: un
 * pedido que nunca existio no tiene por que quedar en la lista. Como este modulo
 * no alimenta ningun calculo de costos ni de ganancias, borrarlo no descuadra
 * nada.
 */
export async function eliminarPedido(id: string): Promise<void> {
  await exigirExiste(db.orders, id, 'el pedido');
  await db.orders.delete(id);
}

// ---------------------------------------------------------------------------
// Consultas
// ---------------------------------------------------------------------------

export async function obtenerPedido(id: string): Promise<Order | undefined> {
  return db.orders.get(id);
}

/** Pedidos de un dia, en el orden en que se capturaron. */
export async function pedidosDeFecha(fechaEntrega: FechaLocal): Promise<Order[]> {
  return ordenar(await db.orders.where('fechaEntrega').equals(fechaEntrega).toArray());
}

/** Rango INCLUSIVO en los dos extremos, igual que el menu. */
export async function pedidosEntre(desde: FechaLocal, hasta: FechaLocal): Promise<Order[]> {
  return ordenar(await db.orders.where('fechaEntrega').between(desde, hasta, true, true).toArray());
}

export async function pedidosDesde(desde: FechaLocal): Promise<Order[]> {
  return ordenar(await db.orders.where('fechaEntrega').aboveOrEqual(desde).toArray());
}

function ordenar(pedidos: Order[]): Order[] {
  return pedidos.sort(
    (a, b) => a.fechaEntrega.localeCompare(b.fechaEntrega) || a.creadoEn.localeCompare(b.creadoEn),
  );
}

// ---------------------------------------------------------------------------
// Vista para la pantalla
// ---------------------------------------------------------------------------

/**
 * El pedido con el nombre de su receta ya resuelto.
 *
 * Es plano y no anida el pedido, a diferencia de otros renglones de este
 * proyecto: asi cumple por si mismo la forma que pide `agruparPorFechaEntrega`
 * del dominio, sin tener que repetir los campos en dos niveles.
 */
export interface RenglonPedido extends Order {
  nombreReceta: string;
  /** true si la receta ya no existe: el pedido se muestra, pero avisa. */
  recetaFaltante: boolean;
}

/**
 * Pedidos agrupados por dia de entrega, con el resumen de cada dia.
 *
 * La agrupacion y los totales los hace el dominio; aqui solo se resuelven los
 * nombres de receta.
 */
export async function verPedidosEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<Array<DiaDePedidos<RenglonPedido>>> {
  return describir(await pedidosEntre(desde, hasta));
}

export async function verPedidosDesde(desde: FechaLocal): Promise<Array<DiaDePedidos<RenglonPedido>>> {
  return describir(await pedidosDesde(desde));
}

/**
 * Un solo dia: sus pedidos y su resumen. Es la consulta de la vista diaria.
 *
 * Devuelve el dia aunque este vacio, con el resumen en ceros, para que la
 * pantalla pueda decir "no hay pedidos para este dia" sin casos especiales.
 */
export async function verPedidosDeFecha(
  fecha: FechaLocal,
): Promise<DiaDePedidos<RenglonPedido>> {
  const dias = await describir(await pedidosDeFecha(fecha));
  return dias[0] ?? { fecha, pedidos: [], resumen: resumenDePedidos([]) };
}

/**
 * Los mismos pedidos del dia, vistos por platillo para servir y rotular.
 *
 * NO es otro registro ni una copia: lee exactamente los pedidos que muestra
 * `verPedidosDeFecha` y solo los agrupa distinto. Marcar una entrega desde aqui
 * usa `alternarEntrega`, que escribe en ese mismo pedido.
 */
export interface EntregasDeFecha {
  fecha: FechaLocal;
  platillos: Array<PlatilloDeEntrega<RenglonPedido>>;
  resumen: ResumenEntregas;
}

export async function verEntregasDeFecha(fecha: FechaLocal): Promise<EntregasDeFecha> {
  const dia = await verPedidosDeFecha(fecha);

  return {
    fecha,
    platillos: agruparPorPlatillo(dia.pedidos),
    resumen: resumenDeEntregas(dia.pedidos),
  };
}

/**
 * Los pedidos de un platillo del menu, agrupados por PRECIO.
 *
 * Existe para registrar la venta con el dinero correcto. Una venta tiene un solo
 * precio unitario, y un dia puede tener varios: el 31 de agosto se entregaron 3
 * pollos, dos a $120 y el de Juan a $100. Registrar 3 a $120 daba $360 cuando lo
 * que se cobro fueron $340.
 *
 * Devuelve un renglon por precio distinto, del mas alto al mas bajo, para poder
 * capturar la venta en tantas partes como precios haya. `registrarVenta()` ya
 * soporta varias capturas del mismo platillo y reparte el costo sin descuadrar.
 */
export interface PedidosAlMismoPrecio {
  precioCentavos: Centavos;
  porciones: number;
  clientes: string[];
}

export async function pedidosPorPrecio(
  plannedItemId: string,
): Promise<PedidosAlMismoPrecio[]> {
  const pedidos = (await db.orders.where('plannedItemId').equals(plannedItemId).toArray()).filter(
    (o) => o.estado === 'activo',
  );

  const porPrecio = new Map<Centavos, PedidosAlMismoPrecio>();
  for (const pedido of pedidos) {
    let renglon = porPrecio.get(pedido.precioPorPorcionCentavos);
    if (renglon === undefined) {
      renglon = {
        precioCentavos: pedido.precioPorPorcionCentavos,
        porciones: 0,
        clientes: [],
      };
      porPrecio.set(pedido.precioPorPorcionCentavos, renglon);
    }
    renglon.porciones += pedido.porciones;

    // Una persona con dos pedidos al mismo precio se nombra una sola vez: la
    // lista es de quienes son, y las porciones ya van aparte.
    const yaEsta = renglon.clientes.some(
      (c) => c.localeCompare(pedido.clienteNombre, 'es', { sensitivity: 'base' }) === 0,
    );
    if (!yaEsta) renglon.clientes.push(pedido.clienteNombre);
  }

  // Del precio mas alto al mas bajo: el de lista primero, los especiales despues.
  return [...porPrecio.values()].sort((a, b) => b.precioCentavos - a.precioCentavos);
}

/**
 * Lo que de verdad esta pedido de cada platillo, por dia de entrega.
 *
 * Responde la duda de la cocina: el menu dice 4 porciones porque asi se planeo,
 * no porque haya 4 pedidos. Sin este dato, "cocina 4 de enchiladas" se lee como
 * si hubiera 4 personas esperando, y se cocina de mas o de menos.
 *
 * La clave es `fechaEntrega|recipeId`, que es como la busca la pantalla de
 * produccion a partir de cada platillo del menu. Solo lectura.
 */
export interface PedidoDeReceta {
  porciones: number;
  cuantos: number;
  /**
   * Lo que se va a cobrar de verdad por estas porciones.
   *
   * Sale de los totales congelados de cada pedido, NO de porciones por el precio
   * del menu: a un cliente de años se le puede respetar otro precio, y entonces
   * los dos numeros no coinciden.
   */
  totalCentavos: Centavos;
  /** Nombre y porciones de cada quien, en el orden en que se capturaron. */
  clientes: Array<{ clienteNombre: string; porciones: number }>;
}

export async function pedidosPorRecetaEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<Map<string, PedidoDeReceta>> {
  const mapa = new Map<string, PedidoDeReceta>();

  for (const pedido of await pedidosEntre(desde, hasta)) {
    if (pedido.estado !== 'activo') continue;

    const clave = `${pedido.fechaEntrega}|${pedido.recipeId}`;
    let entrada = mapa.get(clave);
    if (entrada === undefined) {
      entrada = { porciones: 0, cuantos: 0, totalCentavos: 0, clientes: [] };
      mapa.set(clave, entrada);
    }

    entrada.porciones += pedido.porciones;
    entrada.cuantos += 1;
    entrada.totalCentavos += pedido.totalCentavos;
    entrada.clientes.push({
      clienteNombre: pedido.clienteNombre,
      porciones: pedido.porciones,
    });
  }

  return mapa;
}

/**
 * Cuantos pedidos activos tiene cada dia de un rango, para el selector de dias.
 *
 * Solo cuenta: no resuelve nombres de receta ni totales, porque el selector unicamente
 * necesita saber en que dias hay algo. Los cancelados no cuentan, igual que en
 * el resumen.
 */
export async function conteoPorDia(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<Map<FechaLocal, number>> {
  const conteo = new Map<FechaLocal, number>();

  for (const pedido of await pedidosEntre(desde, hasta)) {
    if (pedido.estado !== 'activo') continue;
    conteo.set(pedido.fechaEntrega, (conteo.get(pedido.fechaEntrega) ?? 0) + 1);
  }

  return conteo;
}

/**
 * Dias que tienen al menos un pedido activo, en orden ascendente.
 *
 * Sirve para decidir que dia abrir. No se limita a una semana a proposito: si el
 * proximo pedido es de dentro de diez dias, la pantalla debe poder ir hasta el.
 */
export async function fechasConPedidos(): Promise<FechaLocal[]> {
  const fechas = new Set<FechaLocal>();

  for (const pedido of await db.orders.toArray()) {
    if (pedido.estado === 'activo') fechas.add(pedido.fechaEntrega);
  }

  return [...fechas].sort((a, b) => a.localeCompare(b));
}

/** Resuelve el nombre de la receta de cada pedido. Una sola lectura para todos. */
async function conNombres(pedidos: Order[]): Promise<RenglonPedido[]> {
  if (pedidos.length === 0) return [];

  const recetas = new Map(
    (await db.recipes.bulkGet([...new Set(pedidos.map((p) => p.recipeId))]))
      .filter((r) => r !== undefined)
      .map((r) => [r.id, r.nombre]),
  );

  return pedidos.map((pedido) => {
    const nombre = recetas.get(pedido.recipeId);
    return {
      ...pedido,
      nombreReceta: nombre ?? '(receta eliminada)',
      recetaFaltante: nombre === undefined,
    };
  });
}

async function describir(pedidos: Order[]): Promise<Array<DiaDePedidos<RenglonPedido>>> {
  return agruparPorFechaEntrega(await conNombres(pedidos));
}

/**
 * Quien debe y cuanto, agrupado por cliente. Solo lectura.
 *
 * Lee TODOS los pedidos, no los de un dia: la deuda es acumulada. Cobrar desde
 * aqui usa `alternarPago`, que escribe en ese mismo pedido.
 */
export async function verCuentasPorCobrar(): Promise<CuentasPorCobrar<RenglonPedido>> {
  return cuentasPorCobrar(await conNombres(await db.orders.toArray()));
}

/** Resumen de un rango completo, para encabezar la pantalla. */
export async function resumenDePedidosEntre(
  desde: FechaLocal,
  hasta: FechaLocal,
): Promise<ResumenPedidos> {
  return resumenDePedidos(await pedidosEntre(desde, hasta));
}

/**
 * Precio que se le cobro por ultima vez a ESTE cliente por ESTE platillo.
 *
 * Existe para los precios de cliente frecuente. Juan compra pollo a la plancha a
 * $100 desde hace años, no a los $120 de la lista, y escribirlo a mano cada vez
 * es como se cuela un cobro equivocado.
 *
 * No es un catalogo de precios por cliente: es la memoria de lo ya capturado. Si
 * nunca se le ha vendido ese platillo devuelve null y manda el precio de la
 * receta, como siempre.
 *
 * Los cancelados si cuentan: el precio que se acordo fue ese, aunque el pedido no
 * se llevara a cabo.
 */
export async function precioUsadoCon(
  clienteNombre: string,
  recipeId: string,
): Promise<Centavos | null> {
  const buscado = claveDeCliente(clienteNombre);
  if (buscado === '') return null;

  /**
   * La comparacion IGNORA mayusculas, acentos y espacios de sobra.
   *
   * El indice de Dexie compara exacto, asi que escribir "juan" no encontraria el
   * historial de "Juan" y el campo ofreceria el precio de lista sin avisar. Un
   * cobro equivocado por una mayuscula es justo lo que este campo evita, asi que
   * se recorren los pedidos y se compara por la clave del cliente.
   *
   * Es la MISMA regla que usan las cuentas por cobrar y la lista de clientes.
   * Antes aqui se comparaba solo en minusculas y las cuentas comparaban exacto:
   * dos reglas distintas para el mismo nombre, que es como nacio el error.
   */
  const suyos = (await db.orders.toArray()).filter(
    (o) => o.recipeId === recipeId && claveDeCliente(o.clienteNombre) === buscado,
  );
  if (suyos.length === 0) return null;

  // El mas reciente por captura: es el acuerdo vigente.
  suyos.sort((a, b) => b.creadoEn.localeCompare(a.creadoEn));
  return suyos[0]?.precioPorPorcionCentavos ?? null;
}

/** Nombres ya usados, para sugerirlos en lugar de escribirlos cada vez. */
export async function clientesUsados(limite = 20): Promise<string[]> {
  const pedidos = await db.orders.toArray();

  /**
   * Se agrupan por clave y no por texto: si escribio "Juan" y "juan", la lista
   * ofrecia las DOS y cada eleccion perpetuaba la variante. Ahora aparece una sola
   * vez, con la escritura que se ve mejor.
   */
  const escrituras = new Map<string, string[]>();

  for (const pedido of pedidos) {
    const clave = claveDeCliente(pedido.clienteNombre);
    if (clave === '') continue;

    const lista = escrituras.get(clave);
    if (lista === undefined) escrituras.set(clave, [pedido.clienteNombre]);
    else lista.push(pedido.clienteNombre);
  }

  return [...escrituras.values()]
    .map((variantes) => ({
      nombre: nombreParaMostrar(variantes),
      cuantos: variantes.length,
    }))
    .sort((a, b) => b.cuantos - a.cuantos || a.nombre.localeCompare(b.nombre, 'es'))
    .slice(0, limite)
    .map((c) => c.nombre);
}

/**
 * Recetas activas para el selector, ordenadas por nombre.
 *
 * Es una lectura: elegir de aqui no modifica la receta ni el menu.
 */
export async function recetasParaPedido(): Promise<Array<{ id: string; nombre: string; precioVentaCentavos: Centavos }>> {
  return (await db.recipes.toArray())
    .filter((r) => r.activo)
    .sort((a, b) => a.nombre.localeCompare(b.nombre, 'es'))
    .map((r) => ({ id: r.id, nombre: r.nombre, precioVentaCentavos: r.precioVentaCentavos }));
}

/**
 * Platillos del menu de un dia, para poder ligar el pedido.
 *
 * Solo lectura, igual que el selector de recetas.
 */
export async function platillosParaLigar(
  fechaEntrega: FechaLocal,
): Promise<Array<{ id: string; recipeId: string; nombre: string; porcionesPlaneadas: number }>> {
  const platillos = await db.plannedItems.where('fechaVenta').equals(fechaEntrega).toArray();
  if (platillos.length === 0) return [];

  const recetas = new Map(
    (await db.recipes.bulkGet([...new Set(platillos.map((p) => p.recipeId))]))
      .filter((r) => r !== undefined)
      .map((r) => [r.id, r.nombre]),
  );

  return platillos.map((p) => ({
    id: p.id,
    recipeId: p.recipeId,
    nombre: recetas.get(p.recipeId) ?? '(receta eliminada)',
    porcionesPlaneadas: p.porcionesPlaneadas,
  }));
}
